import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiUrl } from '../api';

const THEMES = {
  dark:  { bg: '#0f0f1a', text: '#e8e8f0', name: 'Dark' },
  sepia: { bg: '#f4ecd8', text: '#3b2e1e', name: 'Sepia' },
  light: { bg: '#ffffff', text: '#1a1a1a', name: 'Light' },
};

const FONT_SIZES = [14, 16, 18, 20, 22, 26];
const FONTS = ['Georgia, serif', 'Inter, sans-serif', '"Palatino Linotype", serif'];
const FONT_LABELS = ['Serif', 'Sans', 'Palatino'];

function buildSrcdoc(html, theme, fontSize, fontFamily, lineHeight) {
  // Extract body content if it's a full HTML doc
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  const bodyContent = bodyMatch ? bodyMatch[1] : html;

  // Extract existing styles from the book
  const styleMatches = [...html.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)];
  const bookStyles = styleMatches.map((m) => m[1]).join('\n');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  /* Book's own styles */
  ${bookStyles}
  /* Reader override */
  html, body {
    background: ${theme.bg} !important;
    color: ${theme.text} !important;
    font-family: ${fontFamily} !important;
    font-size: ${fontSize}px !important;
    line-height: ${lineHeight} !important;
    max-width: 720px !important;
    margin: 0 auto !important;
    padding: 2rem 1.5rem 6rem !important;
    transition: background 0.2s, color 0.2s;
  }
  a { color: #e94560 !important; text-decoration: none; }
  a:hover { text-decoration: underline; }
  h1, h2, h3, h4, h5, h6 {
    color: ${theme.text} !important;
    margin-top: 2em;
    margin-bottom: 0.5em;
  }
  img { max-width: 100%; height: auto; }
  pre, code { font-size: 0.85em; white-space: pre-wrap; }
  p { margin-bottom: 1em; }
  /* Hide Gutenberg header/nav if present */
  #pg-header, #pg-footer, .navbar, .gutenberg-boilerplate { display: none !important; }
</style>
</head>
<body>${bodyContent}</body>
</html>`;
}

function BookReader({ bookId, title, downloads = {} }) {
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [contentData, setContentData] = useState(null); // { type, content, embedUrl }
  const [theme, setTheme] = useState('dark');
  const [fontSizeIdx, setFontSizeIdx] = useState(2);     // index into FONT_SIZES
  const [fontIdx, setFontIdx] = useState(0);             // index into FONTS
  const [lineHeight, setLineHeight] = useState(1.8);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [srcdoc, setSrcdoc] = useState('');
  const iframeRef = useRef(null);
  const containerRef = useRef(null);

  const fontSize = FONT_SIZES[fontSizeIdx];
  const fontFamily = FONTS[fontIdx];
  const currentTheme = THEMES[theme];

  // Fetch content on mount
  useEffect(() => {
    if (!bookId) return;
    setStatus('loading');
    fetch(apiUrl(`/api/books/content/${bookId}`))
      .then((r) => r.json())
      .then((data) => {
        setContentData(data);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [bookId]);

  // Rebuild srcdoc when reader settings change
  useEffect(() => {
    if (contentData?.type === 'html' && contentData.content) {
      setSrcdoc(buildSrcdoc(contentData.content, currentTheme, fontSize, fontFamily, lineHeight));
    }
  }, [contentData, theme, fontSizeIdx, fontIdx, lineHeight, currentTheme, fontSize, fontFamily]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen?.().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="rounded-xl border border-border bg-surface flex items-center justify-center"
           style={{ height: 400 }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-border border-t-accent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Loading book content...</p>
        </div>
      </div>
    );
  }

  // ── Error / no content ────────────────────────────────────────────────────
  if (status === 'error' || (status === 'ready' && contentData?.type === 'none')) {
    const entries = Object.entries(downloads);
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <p className="text-white font-semibold mb-2">
          {status === 'error' ? 'Could not load book content.' : 'Online reading unavailable for this book.'}
        </p>
        <p className="text-gray-400 text-sm mb-5">Download it to read in your preferred app.</p>
        {entries.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {entries.map(([fmt, url]) => {
              const ext = url.split('.').pop()?.split('?')[0] || fmt;
              const filename = `${title || 'book'}.${ext}`;
              return (
                <a key={fmt}
                   href={`/api/books/proxy-download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`}
                   className="flex items-center gap-2 bg-primary hover:bg-accent/10 border border-border
                              hover:border-accent/60 text-sm text-gray-300 hover:text-accent
                              px-4 py-2.5 rounded-lg transition-all duration-200">
                  ⬇️ {fmt}
                </a>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (status !== 'ready') return null;

  const readerHeight = isFullscreen ? '100vh' : '75vh';

  // ── Toolbar ────────────────────────────────────────────────────────────────
  const toolbar = (
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 flex-wrap"
         style={{ background: '#1a1a2e' }}>
      {/* Title */}
      <span className="text-sm text-white/70 truncate flex-1 min-w-0 font-medium hidden sm:block"
            title={title}>{title}</span>

      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Font size */}
        <div className="flex items-center gap-1 border border-white/10 rounded-lg overflow-hidden">
          <button onClick={() => setFontSizeIdx((i) => Math.max(0, i - 1))}
                  disabled={fontSizeIdx === 0}
                  className="px-2.5 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-white/10
                             disabled:opacity-30 transition-colors">A−</button>
          <span className="px-1 text-xs text-gray-500 select-none">{fontSize}</span>
          <button onClick={() => setFontSizeIdx((i) => Math.min(FONT_SIZES.length - 1, i + 1))}
                  disabled={fontSizeIdx === FONT_SIZES.length - 1}
                  className="px-2.5 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-white/10
                             disabled:opacity-30 transition-colors">A+</button>
        </div>

        {/* Font family */}
        <button onClick={() => setFontIdx((i) => (i + 1) % FONTS.length)}
                className="px-3 py-1.5 text-xs border border-white/10 rounded-lg text-gray-400
                           hover:text-white hover:bg-white/10 transition-colors">
          {FONT_LABELS[fontIdx]}
        </button>

        {/* Line height */}
        <button onClick={() => setLineHeight((h) => h >= 2.4 ? 1.4 : Math.round((h + 0.2) * 10) / 10)}
                className="px-3 py-1.5 text-xs border border-white/10 rounded-lg text-gray-400
                           hover:text-white hover:bg-white/10 transition-colors"
                title="Toggle line spacing">
          ≡
        </button>

        {/* Theme */}
        <div className="flex border border-white/10 rounded-lg overflow-hidden">
          {Object.entries(THEMES).map(([key, t]) => (
            <button key={key} onClick={() => setTheme(key)}
                    title={t.name}
                    className={`w-6 h-6 transition-all ${theme === key ? 'ring-2 ring-accent ring-inset' : 'hover:opacity-80'}`}
                    style={{ background: t.bg, borderRight: '1px solid rgba(255,255,255,0.1)' }} />
          ))}
        </div>

        {/* Fullscreen */}
        <button onClick={toggleFullscreen}
                className="px-3 py-1.5 text-xs border border-white/10 rounded-lg text-gray-400
                           hover:text-white hover:bg-white/10 transition-colors">
          {isFullscreen ? '⤓ Exit' : '⤢ Full'}
        </button>
      </div>
    </div>
  );

  // ── HTML reader (Gutenberg) ────────────────────────────────────────────────
  if (contentData?.type === 'html') {
    return (
      <div ref={containerRef}
           className={`rounded-xl border border-border overflow-hidden ${isFullscreen ? 'fixed inset-0 z-50 rounded-none border-none' : ''}`}
           style={{ height: readerHeight }}>
        {toolbar}
        <iframe
          ref={iframeRef}
          srcDoc={srcdoc}
          title={title}
          className="w-full border-none"
          style={{ height: 'calc(100% - 45px)', background: currentTheme.bg }}
          sandbox="allow-same-origin"
        />
      </div>
    );
  }

  // ── Embed reader (Archive / OpenLibrary) ─────────────────────────────────
  if (contentData?.type === 'embed') {
    return (
      <div ref={containerRef}
           className={`rounded-xl border border-border overflow-hidden ${isFullscreen ? 'fixed inset-0 z-50 rounded-none border-none' : ''}`}
           style={{ height: readerHeight }}>
        {toolbar}
        <iframe
          src={contentData.embedUrl}
          title={title}
          className="w-full border-none"
          style={{ height: 'calc(100% - 45px)' }}
          allowFullScreen
        />
      </div>
    );
  }

  return null;
}

export default BookReader;
